import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useInView } from 'react-intersection-observer';
import { motion } from 'framer-motion';
import { ChevronLeft, ChevronRight } from 'lucide-react';
import { cn } from '@/lib/utils';
import { Button } from '@/components/ui/button';
import { ScrollArea } from '@/components/ui/scroll-area';
import { Sheet, SheetContent, SheetTrigger } from '@/components/ui/sheet';
import { Card, CardContent } from '@/components/ui/card';
import Footer from '@/components/layout/Footer';

// Define the documentation sections
const gradTitle = "text-xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400";
const gradTitleSm = "text-lg font-bold mb-3 text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400";
const sections = [{
  id: 'about',
  title: 'About the Project',
  content: <>
        <Card className="bg-black/40 border-white/5 shadow-lg">
          <CardContent className="p-6 max-h-[70vh] overflow-y-auto scrollbar-custom">
            <h3 className={gradTitle}>🌴 About Wallet Coast Customs</h3>

            <p className="mb-4">Wallet Coast Customs (WCC) is an AI-powered customization studio that paints themed masks on top of Web3 wallets <strong>without modifying the wallets themselves</strong>. The mask is a JSON document generated by Claude, minted as a Solana NFT, and rendered by a transparent macOS overlay that sits on top of the real wallet popup.</p>

            <p className="mb-4">Every keystroke and every click on the mask is forwarded to the real wallet underneath via macOS <code>CGEvent</code>. The wallet itself still does all the security-critical work: password validation, signing, sending transactions. We just paint the skin.</p>

            <h3 className={gradTitle}>🎨 What Works Today</h3>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li><strong>AI Theme Assistant</strong> — describe a theme in natural language ("cyberpunk neon", "Linear minimal", "luxury gold"); Claude Sonnet 4.5 picks one of 14 curated design systems and emits a <code>WCCOverlayV3</code> JSON mask.</li>
              <li><strong>Live mockup preview</strong> in the studio — see the result before minting.</li>
              <li><strong>NFT mint on Solana devnet</strong> — the full theme JSON ships in the NFT's <code>theme_data</code>; the background image lives on IPFS (Lighthouse).</li>
              <li><strong>macOS overlay agent</strong> — a transparent always-on-top Electron window finds the Phantom popup, paints the mask, and forwards your input to Phantom via CGEvent injection.</li>
              <li><strong>Auto-hide</strong> when Phantom navigates past the password screen — we don't have masks for the home/send/receive screens yet, so the overlay steps out of the way.</li>
            </ul>

            <h3 className={gradTitle}>🧩 Architecture in one sentence</h3>

            <p className="mb-4">Lovable web app (this site) → AI orchestrator (Supabase Edge Function calling Claude) → Supabase Realtime broadcast → local sync-server on the user's Mac → Electron overlay agent painting on Phantom popup. Source code is split across two public repos: <a href="https://github.com/Alexanfr-God/solana-style-studio" className="text-purple-300 hover:text-purple-200 underline">solana-style-studio</a> (web + AI) and <a href="https://github.com/Alexanfr-God/phantom-overlay-editor" className="text-purple-300 hover:text-purple-200 underline">phantom-overlay-editor</a> (macOS overlay).</p>

            <div className="p-4 bg-gradient-to-r from-black/60 to-solana-purple/10 rounded-md mb-4">
              <h3 className={gradTitle}>🌊 Why This Matters</h3>
              <p className="mb-2">Wallets are the most-touched surface in Web3. Today they all look the same. Letting users own and apply their own visual mask — as an NFT — turns the wallet into part of their on-chain identity, without asking the wallet team to change a single line of code.</p>
            </div>
          </CardContent>
        </Card>
      </>
}, {
  id: 'roadmap',
  title: 'Roadmap',
  content: <>
        <Card className="bg-black/40 border-white/5 shadow-lg">
          <CardContent className="p-6 max-h-[70vh] overflow-y-auto scrollbar-custom">
            <h2 className={gradTitle}>🚗 WCC Roadmap — honest version</h2>

            <p className="mb-6 text-white/80">We're keeping this roadmap deliberately concrete for "Now" and deliberately vague-with-conditions for "Later" — wallet integrations depend on wallet teams agreeing to support overlay masks, and we don't want to promise dates we don't control.</p>

            <div className="space-y-6">
              <div>
                <p className="mb-2 text-lg font-semibold">🔹 <span className={gradTitle.replace('mb-4', '')}>Now — Hackathon demo</span></p>
                <p className="mb-2 italic text-white/70">"It actually works, just narrowly."</p>
                <ul className="list-disc pl-5 mb-2 space-y-1">
                  <li>✅ macOS overlay agent paints the themed mask on Phantom's password screen</li>
                  <li>✅ AI orchestrator with Claude Sonnet 4.5 + 14 curated design systems</li>
                  <li>✅ NFT mint on Solana devnet — full theme JSON stored in <code>theme_data</code></li>
                  <li>✅ Minted Gallery with one-click "Apply" → Supabase Realtime → live overlay</li>
                  <li>✅ Auto-hide when Phantom navigates past the password screen</li>
                  <li>⚙️ Input forwarding (keystrokes + Unlock click → real Phantom) — proof-of-concept working; not yet hardened for production use</li>
                </ul>
              </div>

              <div>
                <p className="mb-2 text-lg font-semibold">🔸 <span className={gradTitle.replace('mb-4', '')}>Next — Harden the overlay</span></p>
                <p className="mb-2 italic text-white/70">"Make the one screen we have rock-solid; add more screens."</p>
                <ul className="list-disc pl-5 mb-2 space-y-1">
                  <li>Stabilize input forwarding — make password typing + Unlock click reliable across edge cases (different popup sizes, dock placements, multi-monitor)</li>
                  <li>Masks for more Phantom screens: home (balance + tokens), send, receive, NFT view</li>
                  <li>Better element-by-element customization vocabulary in the AI orchestrator</li>
                  <li>Marketplace prototype for trading themes</li>
                  <li>Cleaner onboarding (drop the manual Accessibility permission step)</li>
                </ul>
              </div>

              <div>
                <p className="mb-2 text-lg font-semibold">⚪ <span className={gradTitle.replace('mb-4', '')}>Aspirational — Real wallet integrations</span></p>
                <p className="mb-2 italic text-white/70">"If wallet teams choose to support overlay masks."</p>
                <ul className="list-disc pl-5 mb-2 space-y-1">
                  <li>Native partnerships with wallet teams (Phantom, Solflare, Backpack, others). <strong>This depends on their willingness.</strong> We can't ship native integration unilaterally — wallets would need to opt in.</li>
                  <li>Other platforms: Windows, Linux. The current overlay is Swift/CoreGraphics; each platform needs its own native event injection.</li>
                  <li>Mobile wallets. Out of scope for the overlay-on-top approach; would require a different architecture.</li>
                  <li>WCC Theme Protocol — an open standard so any wallet can declare its anchors and accept themed masks. Only valuable if multiple wallets choose to implement it.</li>
                </ul>
                <p className="mt-3 text-white/70 text-sm">We won't quote dates for the aspirational tier — those depend on conversations with wallet teams that haven't happened yet.</p>
              </div>
            </div>

            <div className="mt-6 p-4 bg-black/40 border border-white/10 rounded-lg">
              <h3 className={gradTitleSm}>⏱️ Stage Status</h3>
              <div className="space-y-2">
                <div className="grid grid-cols-3 gap-4 font-semibold text-white/90 pb-2 border-b border-white/20">
                  <span>Stage</span>
                  <span>Timing</span>
                  <span>Status</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-white/80">
                  <span>Phantom password mask + mint demo</span>
                  <span>Now</span>
                  <span>✅ Live (devnet)</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-white/80">
                  <span>Stabilize input forwarding + more screens</span>
                  <span>Next phase</span>
                  <span>⚙️ In progress</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-white/80">
                  <span>Theme marketplace + production-grade mint</span>
                  <span>Later in 2026</span>
                  <span>📦 Designed, not built</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-white/80">
                  <span>Native wallet partnerships</span>
                  <span>If/when wallets agree</span>
                  <span>⚪ Aspirational</span>
                </div>
                <div className="grid grid-cols-3 gap-4 text-white/80">
                  <span>Windows/Linux overlay agent</span>
                  <span>If/when there's demand</span>
                  <span>⚪ Aspirational</span>
                </div>
              </div>
            </div>
          </CardContent>
        </Card>
      </>
}, {
  id: 'mint',
  title: 'How Mint Works',
  content: <>
        <Card className="bg-black/40 border-white/5 shadow-lg">
          <CardContent className="p-6 max-h-[70vh] overflow-y-auto scrollbar-custom">
            <h3 className={gradTitle}>🎨 Minting today (Solana devnet)</h3>

            <p className="mb-4">A WCC theme is born as a <code>WCCOverlayV3</code> JSON document. When the user mints, the theme becomes a Metaplex NFT with:</p>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li><strong>Preview PNG</strong> — a rendered snapshot of the mockup wallet wearing the theme, stored on IPFS via Lighthouse.</li>
              <li><strong>Full theme JSON</strong> — inlined into the NFT's <code>theme_data</code> column on Supabase + mirrored on IPFS. Storing it on-chain-adjacent means "Apply" is one click and zero IPFS round-trips for cached themes.</li>
            </ul>

            <h3 className={gradTitleSm}>What the theme JSON actually contains</h3>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li><code>global.background</code> — image URL (Gemini-generated, stored on Supabase Storage) + optional animation preset</li>
              <li><code>global.color_analysis</code> — derived palette: safe text color, safe accent, forbidden colors, dominant hues</li>
              <li><code>elements</code> — per-anchor styling: <code>background-layer</code>, <code>header</code>, <code>balance-sol</code>, <code>btn-buy</code>, <code>btn-send</code>, etc. Each has style (fill, border, radius, shadow), text (color, size, weight, fontFamily, letter-spacing, textShadow), and optional animation</li>
              <li><code>logo</code> — points at a canonical Phantom-style ghost SVG; theme adds glow filter, opacity, animation on top</li>
            </ul>

            <h3 className={gradTitleSm}>⚙️ Mint flow</h3>

            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">1.</span>
                <div>
                  <p className="font-semibold">AI Theme Assistant generates the WCCOverlayV3 JSON</p>
                  <p className="text-sm text-white/70">Claude Sonnet 4.5 + background-agent (Gemini Image Preview)</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">2.</span>
                <div>
                  <p className="font-semibold">User previews the mockup, optionally tweaks via element-edit chat</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">3.</span>
                <div>
                  <p className="font-semibold">Preview PNG + theme JSON pinned to IPFS</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">4.</span>
                <div>
                  <p className="font-semibold">User signs the Metaplex mint transaction with their Solana wallet</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">5.</span>
                <div>
                  <p className="font-semibold">NFT appears in the Minted Gallery with Apply / ⚡ buttons</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">6.</span>
                <div>
                  <p className="font-semibold">⚡ broadcasts the layout via Supabase Realtime</p>
                  <p className="text-sm text-white/70">Local overlay agent receives, paints on Phantom popup</p>
                </div>
              </div>
            </div>

            <h3 className={gradTitleSm}>🎯 Current status</h3>
            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li>✅ Devnet mint working end-to-end</li>
              <li>✅ Apply ⚡ → overlay on Phantom popup</li>
              <li>⚙️ Production-grade mint — next phase, after the demo is hardened</li>
              <li>⚪ Marketplace, royalty enforcement — designed but not built (see Royalties section)</li>
            </ul>
          </CardContent>
        </Card>
      </>
}, {
  id: 'royalties',
  title: 'Royalties & Profit Split',
  content: <>
        <Card className="bg-black/40 border-white/5 shadow-lg">
          <CardContent className="p-6 max-h-[70vh] overflow-y-auto scrollbar-custom">
            <h3 className={gradTitle}>🤝 The fair-share idea</h3>

            <p className="mb-4">A minted WCC theme is designed to support multi-tier royalties so every contributor benefits when a theme is resold:</p>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li><strong>Creator</strong> — ongoing royalty on every secondary sale.</li>
              <li><strong>WCC platform</strong> — small service fee covering AI inference, IPFS pinning, infra.</li>
              <li><strong>Integrated wallet partner</strong> — a percentage that goes to the wallet team whose UI the mask paints on.</li>
            </ul>

            <h3 className={gradTitleSm}>🚧 What is enforced today vs aspirational</h3>

            <div className="grid grid-cols-3 gap-4 font-semibold text-white/90 pb-2 border-b border-white/20 mb-2">
              <span>Tier</span>
              <span>Status</span>
              <span>When</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-white/80 mb-1">
              <span>Mint fee (one-time, devnet)</span>
              <span>✅ Live</span>
              <span>Now</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-white/80 mb-1">
              <span>Creator royalty (Metaplex standard)</span>
              <span>⚙️ Encoded in NFT, marketplace-dependent</span>
              <span>Now (devnet)</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-white/80 mb-1">
              <span>WCC platform fee on secondary sales</span>
              <span>📦 Designed, not built</span>
              <span>After marketplace ships</span>
            </div>
            <div className="grid grid-cols-3 gap-4 text-white/80 mb-1">
              <span>Wallet partner fee</span>
              <span>⚪ Aspirational</span>
              <span>Only after a wallet integration agreement</span>
            </div>

            <h3 className={gradTitleSm}>What creators will be able to do</h3>
            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li>Set their own royalty percentage at mint time</li>
              <li>Launch one-of-one or open-edition drops</li>
              <li>Track mint stats, resales, and applies via the Minted Gallery</li>
            </ul>

            <p className="mb-2 text-white/70 text-sm">Once we ship our own marketplace (planned 2026), the platform and creator splits will be enforced at the contract level. Until then, the only enforced split is whatever the user sets at mint via Metaplex.</p>
          </CardContent>
        </Card>
      </>
}, {
  id: 'architecture',
  title: 'Architecture & Tech Stack',
  content: <>
        <Card className="bg-black/40 border-white/5 shadow-lg">
          <CardContent className="p-6 max-h-[70vh] overflow-y-auto scrollbar-custom">
            <h3 className={gradTitle}>🧱 Three-piece architecture</h3>

            <pre className="bg-black/60 border border-white/10 rounded-md p-3 mb-4 text-xs text-white/80 overflow-x-auto">{`solana-style-studio (Lovable)        ─┐
  • React + TS + Tailwind             │
  • AI Theme Assistant                │  Supabase Realtime
  • wccToLayoutDocument.ts            │  channel "wcc:overlay"
  • theme-orchestrator (Edge Function)│
                                       │
                                       ▼
Supabase ──────────────────────────────▶  phantom-overlay-editor
  • Postgres (themes, mints, RLS)        (runs on user's Mac)
  • Edge Functions (Deno)              ┌─────────────────────┐
  • Realtime (WS broadcast)            │ sync-server :3333   │
  • Storage (preview PNGs)             │ agent (Electron+    │
                                       │   Swift CGEvent)    │
                                       │ desktop (admin)     │
                                       └─────────────────────┘
                                                │
                                                ▼  paints on top of
                                       Phantom Chrome popup
                                       (unmodified)`}</pre>

            <h3 className={gradTitleSm}>📦 Tech stack</h3>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li><strong>Web frontend (Lovable):</strong> React 18 + TypeScript + Tailwind + Zustand for state + react-router</li>
              <li><strong>Backend:</strong> Supabase Postgres (with RLS) + Supabase Edge Functions (Deno) + Supabase Realtime + Supabase Storage</li>
              <li><strong>AI:</strong> Anthropic Claude Sonnet 4.5 (<code>claude-sonnet-4-5</code>) via Messages API for the orchestrator; Gemini Image Preview via Lovable AI gateway for background image generation</li>
              <li><strong>Blockchain:</strong> Solana web3.js + Metaplex (Token Metadata standard for NFT mint)</li>
              <li><strong>Storage:</strong> IPFS via Lighthouse for the preview PNG; full theme JSON inlined in <code>theme_data</code> for fast Apply</li>
              <li><strong>Overlay agent:</strong> Electron + React renderer + Swift CoreGraphics binary for window detection and event injection</li>
              <li><strong>Transport:</strong> Supabase Realtime broadcast → sync-server (Node) → WebSocket on localhost:3333 → Electron main process IPC</li>
              <li><strong>Schemas:</strong> Zod for WS message validation across packages</li>
            </ul>

            <h3 className={gradTitleSm}>🧬 The WCCOverlayV3 JSON contract</h3>

            <p className="mb-2">A theme is a JSON document with three top-level pieces:</p>
            <pre className="bg-black/60 border border-white/10 rounded-md p-3 mb-4 text-xs text-white/80 overflow-x-auto">{`{
  version: 3,
  wallet: "phantom",
  theme_name: "Cosmic Crystal",
  generated_at: "2026-05-07T...",
  global: {
    background: { type: "image", url: "...", animation_preset?: "..." },
    color_analysis: { dominant, luminance, safe_text, safe_accent, palette, ... }
  },
  elements: {
    "background-layer": { style: {...} },
    "header":           { style: {...}, text: {...} },
    "balance-sol":      { style: {...}, text: {...}, animation: {...} },
    "btn-buy":          { style: {...}, text: {...}, icon: {...} },
    // ... ~14 elements total
  }
}`}</pre>

            <p className="mb-4 text-white/70 text-sm">The orchestrator emits this JSON. <code>buildThemeOverrides()</code> in <code>phantomThemeStore.ts</code> converts each element to a CSS object. <code>wccToLayoutDocument()</code> wraps it into the broadcast layout shape (anchor positions + CSS) that the overlay agent renders.</p>

            <h3 className={gradTitleSm}>💻 Run it locally (hackathon judges)</h3>

            <p className="mb-2 text-white/80">macOS only for now. Two repos, four commands total.</p>

            <p className="mb-2 text-white/80 font-semibold">1. The overlay agent (runs on your Mac):</p>
            <pre className="bg-black/60 border border-white/10 rounded-md p-3 mb-3 text-xs text-white/80 overflow-x-auto">{`git clone git@github.com:Alexanfr-God/phantom-overlay-editor.git
cd phantom-overlay-editor
npm install

# In three terminals (or backgrounded):
npm run dev:server    # sync-server on ws://localhost:3333
npm run dev:agent     # macOS overlay (Swift compile ~30-60s first run)
npm run dev:desktop   # admin window with opacity slider`}</pre>

            <p className="mb-2 text-white/80 font-semibold">2. The web studio (already deployed on Lovable, but you can run it locally too):</p>
            <pre className="bg-black/60 border border-white/10 rounded-md p-3 mb-3 text-xs text-white/80 overflow-x-auto">{`git clone git@github.com:Alexanfr-God/solana-style-studio.git
cd solana-style-studio
npm install
cp .env.example .env       # paste VITE_SUPABASE_URL + VITE_SUPABASE_ANON_KEY
npm run dev                # opens at http://localhost:8080`}</pre>

            <p className="mb-2 text-white/70 text-sm"><strong>First run gotcha:</strong> the first time the agent tries to inject a keystroke, macOS pops the Accessibility permission dialog. Approve <code>Electron</code> in System Preferences → Privacy & Security → Accessibility, then restart <code>npm run dev:agent</code>.</p>

            <p className="mb-2 text-white/70 text-sm">Once both sides are running: open Phantom popup in Chrome → click ⚡ on any minted theme in the WCC gallery → the overlay paints on top of Phantom. Full instructions and troubleshooting are in each repo's README.</p>

          </CardContent>
        </Card>
      </>
}, {
  id: 'ai-model',
  title: 'AI Model & Prompting Strategy',
  content: <>
        <Card className="bg-black/40 border-white/5 shadow-lg">
          <CardContent className="p-6 max-h-[70vh] overflow-y-auto scrollbar-custom">
            <h3 className={gradTitle}>🧠 Claude Sonnet 4.5 as the brain</h3>

            <p className="mb-4">All text-mode agents use Anthropic Claude Sonnet 4.5 via the Messages API with a low temperature (~0.4) to keep output deterministic. The orchestrator is a six-stage pipeline so each agent has a narrow, testable job.</p>

            <h3 className={gradTitleSm}>🪜 The six-stage pipeline</h3>

            <div className="space-y-3 mb-4">
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">1.</span>
                <div>
                  <p className="font-semibold">design-system-selector</p>
                  <p className="text-sm text-white/70">Reads the user prompt + 14 system descriptions, returns one <code>id</code>. Picks "cyberpunk" for "neon glow", "linear" for "minimal dev tool", etc. Defaults to <code>dark-minimal</code> when uncertain.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">2.</span>
                <div>
                  <p className="font-semibold">background-agent (Gemini Image Preview)</p>
                  <p className="text-sm text-white/70">Generates the background image. We hand it the design system + user prompt, store the result in Supabase Storage. Falls back to a 3-stop animated gradient if generation fails.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">3.</span>
                <div>
                  <p className="font-semibold">color-analyst</p>
                  <p className="text-sm text-white/70">Claude vision call — reads the background image, returns palette: <code>dominant</code>, <code>luminance</code>, <code>safe_text</code>, <code>safe_accent</code>, <code>forbidden</code>. Constraint: 4.5:1 minimum contrast ratio for text.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">4.</span>
                <div>
                  <p className="font-semibold">designer (DesignBrief)</p>
                  <p className="text-sm text-white/70">Reads design system + color analysis + prompt, emits a high-level brief: mood, typography style, component philosophy. The "creative director" step.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">5.</span>
                <div>
                  <p className="font-semibold">element-designer</p>
                  <p className="text-sm text-white/70">The biggest call — generates per-element styles for all ~14 anchors. System prompt explicitly enumerates the customization vocabulary (fill, gradient, border, radius, shadow, filter, transform, text color/size/weight/fontFamily/letterSpacing/textShadow, animation type+duration+intensity, icon tint). Strict prohibitions: no neon flood, no full-saturation fills, no pulse on body text, max 4 animated elements per theme.</p>
                </div>
              </div>
              <div className="flex items-start gap-3">
                <span className="text-purple-400 font-bold">6.</span>
                <div>
                  <p className="font-semibold">director (review pass)</p>
                  <p className="text-sm text-white/70">Reads the brief + the assembled theme, returns a similarity score and a punch list of misses. Currently used for telemetry; the feedback-loop iteration is on the roadmap.</p>
                </div>
              </div>
            </div>

            <h3 className={gradTitleSm}>📚 The 14 design systems</h3>

            <p className="mb-2 text-white/80">Each is a curated <code>DESIGN.md</code> file (~3KB) that the selector picks one of and threads into <code>designer</code> + <code>element-designer</code> as system context:</p>

            <div className="grid grid-cols-2 md:grid-cols-3 gap-2 mb-4 text-sm">
              {['linear', 'stripe', 'vercel', 'coinbase', 'robinhood', 'cash-app', 'apple', 'notion', 'raycast', 'elevenlabs', 'cyberpunk', 'vaporwave', 'luxury-gold', 'dark-minimal'].map((id) => (
                <div key={id} className="px-3 py-1.5 bg-black/40 border border-white/10 rounded text-white/80 font-mono">{id}.md</div>
              ))}
            </div>

            <p className="mb-2 text-white/70 text-sm">Each file has a fixed 9-section structure: visual theme, color roles, typography, component styles, layout, hierarchy, motion, wallet-specific guidance (password screen), and explicit don'ts. Adding a new system = drop in a new <code>{`<id>.md`}</code> file and register it in <code>design-systems/index.ts</code>.</p>

            <h3 className={gradTitleSm}>🛡️ Why the orchestrator is multi-stage</h3>

            <p className="mb-2">A single prompt asking "design a theme" produces mush. Splitting the job — pick aesthetic, then generate background, then sample palette, then write brief, then style each element — gives us testable intermediate outputs, a place to drop deterministic fallbacks (gradient backgrounds when image gen times out), and the ability to swap models per stage if needed.</p>
          </CardContent>
        </Card>
      </>
}, {
  id: 'monetization',
  title: 'Monetization Strategy',
  content: <>
        <Card className="bg-black/40 border-white/5 shadow-lg">
          <CardContent className="p-6 max-h-[70vh] overflow-y-auto scrollbar-custom">
            <h3 className={gradTitle}>💰 Revenue surfaces</h3>

            <ol className="list-decimal pl-5 mb-4 space-y-2">
              <li><strong>Mint fees.</strong> Small SOL fee on every theme mint. Funds AI inference (Claude + Gemini), IPFS pinning, Supabase infra.</li>
              <li><strong>Secondary marketplace fee.</strong> Once our marketplace ships, WCC takes a small cut of every theme resale.</li>
              <li><strong>Creator royalties.</strong> Set per-theme at mint via Metaplex standard. Enforced on any marketplace that respects Metaplex royalties.</li>
              <li><strong>Wallet partner fees.</strong> If/when a wallet team agrees to natively recognize WCC masks (so users don't need our overlay agent), that wallet gets a percentage of each Apply/mint involving their wallet.</li>
              <li><strong>Premium AI tier (later).</strong> Higher-quality / bespoke theme generation, custom design-system uploads, batch generation. Built on the same orchestrator with a different model + token budget.</li>
            </ol>

            <h3 className={gradTitleSm}>🚧 Honest current state</h3>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li>✅ Devnet mint flow is live; the SOL "fee" right now is just transaction cost on devnet.</li>
              <li>⚙️ Production-grade mint with a real platform fee — next phase, after the demo is hardened.</li>
              <li>📦 Marketplace + secondary fees — designed (data model exists), not built.</li>
              <li>⚪ Wallet partner fees — depend on partnerships that haven't happened. We won't activate this revenue tier until we have an actual wallet integration agreement.</li>
            </ul>

            <h3 className={gradTitleSm}>📈 Unit economics (back-of-envelope)</h3>

            <p className="mb-2">Per theme generation cost ≈ ~$0.13 in AI tokens (Claude pipeline) + ~$0.04 for the background image (Gemini) + Supabase + IPFS pinning ≈ <strong>under $0.20 marginal cost</strong>. Target mint fee: somewhere between <strong>$5 and $8</strong> in SOL equivalent — that's the price point that funds the platform (AI inference + infra + creator support) while staying affordable for users minting their wallet identity. Healthy margin per mint at the operating-cost layer; fixed costs (engineering, AI ops) covered out of that margin at meaningful volume.</p>
          </CardContent>
        </Card>
      </>
}, {
  id: 'community',
  title: 'Community & Growth Plan',
  content: <>
        <Card className="bg-black/40 border-white/5 shadow-lg">
          <CardContent className="p-6 max-h-[70vh] overflow-y-auto scrollbar-custom">
            <h3 className={gradTitle}>🌐 Open-source approach</h3>

            <p className="mb-4">Both repos are public on GitHub: <a href="https://github.com/Alexanfr-God/solana-style-studio" className="text-purple-300 hover:text-purple-200 underline">solana-style-studio</a> (web + AI) and <a href="https://github.com/Alexanfr-God/phantom-overlay-editor" className="text-purple-300 hover:text-purple-200 underline">phantom-overlay-editor</a> (macOS overlay). Anyone can clone, run the demo, and read the orchestrator code to see exactly what the AI does.</p>

            <h3 className={gradTitleSm}>👥 Where contributors can help</h3>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li><strong>New design systems.</strong> Drop a new <code>DESIGN.md</code> in the orchestrator's <code>design-systems/</code> folder following the 9-section template. The selector picks it up automatically.</li>
              <li><strong>New wallet screens.</strong> Define anchors for Phantom's home / send / NFT-view screens, write the layout-doc + renderer mapping. PRs welcome in both repos.</li>
              <li><strong>Cross-platform overlay agents.</strong> The Mac agent uses Swift CGEvent. A Linux agent could use XTEST + XComposite, Windows could use SendInput. Each is a self-contained sibling package.</li>
              <li><strong>AI prompt engineering.</strong> The orchestrator system prompts live in <code>agents/*.ts</code> — concrete, readable. Tighten them with explicit do's and don'ts; submit before/after theme screenshots.</li>
            </ul>

            <h3 className={gradTitleSm}>📣 Distribution plan</h3>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li><strong>Hackathon demo.</strong> Live walk-through: prompt → mint → overlay on Phantom popup → unlock. The transparent overlay is unusual and remembered.</li>
              <li><strong>Theme creator beta.</strong> Invite designers to mint their first WCC theme; promote stand-out themes on the Minted Gallery landing page.</li>
              <li><strong>Wallet team outreach.</strong> The transparent-overlay approach lets us demo to wallet teams without asking them to ship anything. If they like the result, we can talk integration.</li>
              <li><strong>Twitter + Discord + farcaster placeholders.</strong> Channels to be set up post-hackathon.</li>
            </ul>

            <h3 className={gradTitleSm}>🤝 What we're NOT promising</h3>

            <p className="mb-2 text-white/70 text-sm">No claims of partnership with any wallet, no "5 wallets confirmed by Q2", no DAO. The community plan is open-source, demos, and outreach — measured by GitHub stars, theme mints, and conversations with wallet teams, not by inflated TVL/MAU numbers.</p>
          </CardContent>
        </Card>
      </>
}, {
  id: 'security',
  title: 'Security & Ownership',
  content: <>
        <Card className="bg-black/40 border-white/5 shadow-lg">
          <CardContent className="p-6 max-h-[70vh] overflow-y-auto scrollbar-custom">
            <h3 className={gradTitle}>🧱 What we've built so far</h3>

            <p className="mb-4">Today the overlay covers <strong>one screen</strong>: the Phantom password / lock screen. That's the foundation. We picked the simplest, most-visible surface to prove the architecture — and to show it can be safe by design. Everything beyond this screen is the next phase.</p>

            <p className="mb-2 text-white/80 font-semibold">Layers ahead (Phantom wallet surface):</p>
            <ul className="list-disc pl-5 mb-4 space-y-1 text-white/80">
              <li>Home screen — balance, USD value, token quick-actions</li>
              <li>Token list — your portfolio rendered in the themed palette</li>
              <li>New asset containers — when you buy a new token, it appears in the same themed style</li>
              <li>NFT collections view</li>
              <li>Send / Receive flows</li>
              <li>Swap flow</li>
              <li>Settings + account dropdown</li>
            </ul>

            <p className="mb-4 text-white/70 text-sm">Each new layer will inherit the same safety properties listed below, because they all live inside the same overlay-on-top architecture — none of them need to touch wallet internals.</p>

            <h3 className={gradTitleSm}>🔐 Safety by design (these don't change as we add layers)</h3>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li><strong>We never see your private keys.</strong> They live in Phantom's own storage. We don't read, intercept, or copy them.</li>
              <li><strong>We never sign anything.</strong> Every signature stays with Phantom. Our overlay paints — it doesn't call wallet APIs.</li>
              <li><strong>We never modify the wallet.</strong> Phantom's source is untouched. We sit on top in a transparent window; that's it.</li>
              <li><strong>Everything runs locally.</strong> The overlay agent + sync-server run on your own Mac. Themes are pulled from Supabase Realtime, but your interactions with the wallet never leave your machine.</li>
              <li><strong>You own the mask.</strong> Themes are Metaplex NFTs on Solana. Owning the NFT means owning the theme — the JSON travels with it (in <code>theme_data</code> + IPFS), so leaving the platform doesn't take your mask away.</li>
            </ul>

            <p className="mb-4 text-white/70 text-sm"><strong>Honest disclaimer:</strong> we've shown the architecture is safe in principle, but the implementation is a hackathon proof-of-concept — not yet audited or hardened for production use. We recommend devnet / test wallets only until a formal security review is done. That review is on the roadmap before production deployment.</p>

            <h3 className={gradTitleSm}>Database-side hygiene</h3>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li><strong>Row Level Security (RLS)</strong> on every Supabase table — themes, mints, telemetry are scoped to the owning wallet address.</li>
              <li><strong>Edge Function secrets</strong> live in Supabase Secrets — never embedded in the client.</li>
              <li><strong>WebSocket message validation</strong> via Zod schemas — malformed broadcasts are dropped before they reach the agent.</li>
              <li><strong>AI output validation</strong> — every <code>WCCOverlayV3</code> from the orchestrator is type-checked before being saved or broadcast.</li>
            </ul>

            <h3 className={gradTitleSm}>What an attacker still can't do</h3>

            <ul className="list-disc pl-5 mb-4 space-y-2">
              <li>Steal your funds by compromising WCC — they'd still need to break Phantom independently.</li>
              <li>Replace your theme on-chain — only the NFT owner can re-mint or transfer it.</li>
              <li>Push a malicious theme that drains the wallet — themes are JSON describing visual styling; they cannot trigger transactions.</li>
            </ul>

            <h3 className={gradTitleSm}>One permission we ask for</h3>

            <p className="mb-2 text-white/80">macOS Accessibility permission for the overlay app — needed so the local agent can paint and forward your input to the real wallet. It's the same permission productivity tools like Raycast or Rectangle ask for. You grant it once in System Preferences → Privacy & Security → Accessibility, and only when you're running the demo locally.</p>
          </CardContent>
        </Card>
      </>
}];

const DocSidebar = ({
  activeSection,
  setActiveSection,
  className
}: {
  activeSection: string;
  setActiveSection: (id: string) => void;
  className?: string;
}) => {
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  return <motion.div ref={ref} initial={{
    opacity: 0,
    x: -20
  }} animate={{
    opacity: inView ? 1 : 0,
    x: inView ? 0 : -20
  }} transition={{
    duration: 0.5,
    delay: 0.2
  }} className={cn("w-full lg:w-64 shrink-0", className)}>
      <h2 className="text-xl font-bold mb-6 text-center lg:text-left text-transparent bg-clip-text bg-gradient-to-r from-purple-400 to-blue-400">
        WCC Documentation
      </h2>
      
      
      <div className="space-y-1">
        {sections.map(section => <button key={section.id} onClick={() => setActiveSection(section.id)} className={cn("w-full text-left py-3 px-4 rounded-lg transition-all duration-300", "hover:bg-white/10 hover:shadow-[0_0_10px_rgba(153,69,255,0.2)]", activeSection === section.id ? "bg-white/5 shadow-[0_0_15px_rgba(153,69,255,0.3)] border border-purple-500/30" : "border border-transparent")}>
            <span className={cn("transition-all duration-300", activeSection === section.id ? "text-white text-shadow-glow" : "text-white/70")}>
              {section.title}
            </span>
          </button>)}
      </div>
    </motion.div>;
};
const DocContent = ({
  activeSection
}: {
  activeSection: string;
}) => {
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: false,
    threshold: 0.1
  });
  const activeContent = sections.find(section => section.id === activeSection);
  return <motion.div ref={ref} initial={{
    opacity: 0,
    y: 20
  }} animate={{
    opacity: inView ? 1 : 0,
    y: inView ? 0 : 20
  }} transition={{
    duration: 0.5
  }} className="flex-1 bg-black/30 backdrop-blur-md border border-white/10 rounded-xl p-6">
      <div className="mb-8">
        <h2 className="text-2xl font-bold mb-4 text-transparent bg-clip-text bg-gradient-to-r from-solana-purple to-solana-blue">
          {activeContent?.title}
        </h2>
        <div className="text-white/80 leading-relaxed">
          {activeContent?.content}
        </div>
      </div>
    </motion.div>;
};
const Documentation = () => {
  const [activeSection, setActiveSection] = useState('about');
  const navigate = useNavigate();

  // Create a page entry animation effect
  const {
    ref,
    inView
  } = useInView({
    triggerOnce: true,
    threshold: 0.1
  });
  useEffect(() => {
    // Scroll to top when page loads
    window.scrollTo(0, 0);
  }, []);
  return <motion.div ref={ref} initial={{
    opacity: 0
  }} animate={{
    opacity: inView ? 1 : 0
  }} transition={{
    duration: 0.7
  }} className="min-h-screen flex flex-col bg-gradient-to-br from-black via-purple-950/20 to-black">
      {/* Ambient glow effects */}
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_top_right,rgba(153,69,255,0.15),transparent_50%)] pointer-events-none"></div>
      <div className="fixed inset-0 bg-[radial-gradient(ellipse_at_bottom_left,rgba(20,241,149,0.1),transparent_50%)] pointer-events-none"></div>
      
      <div className="relative z-10 container mx-auto px-4 py-8">
        {/* Back button */}
        <div className="flex justify-between items-center mb-8">
          <Button onClick={() => navigate('/')} variant="ghost" className="text-white/80 hover:text-white hover:bg-white/10">
            <ChevronLeft className="mr-1 w-5 h-5" />
            Back to Home
          </Button>
          
          {/* Mobile sidebar trigger */}
          <Sheet>
            <SheetTrigger asChild>
              <Button variant="outline" className="lg:hidden text-white border-white/20 bg-black/30 backdrop-blur-sm">
                Sections <ChevronRight className="ml-1 w-4 h-4" />
              </Button>
            </SheetTrigger>
            <SheetContent side="right" className="bg-black/95 border-white/10 w-[85%] max-w-[320px]">
              <DocSidebar activeSection={activeSection} setActiveSection={setActiveSection} className="w-full mt-8" />
            </SheetContent>
          </Sheet>
        </div>
        
        {/* Main content */}
        <div className="flex flex-col lg:flex-row gap-8 pb-16">
          {/* Desktop sidebar - hidden on mobile */}
          <div className="hidden lg:block">
            <DocSidebar activeSection={activeSection} setActiveSection={setActiveSection} />
          </div>
          
          {/* Content area */}
          <ScrollArea className="flex-1">
            <DocContent activeSection={activeSection} />
          </ScrollArea>
        </div>
      </div>
      
      <div className="mt-auto">
        <Footer />
      </div>
    </motion.div>;
};
export default Documentation;
